import { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { useQuery } from 'react-query';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import Container from '../../components/Container';
import DocumentsViewToggle, {
  useDocumentsView,
} from '../../components/DocumentsViewToggle';
import ListPagination, {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from '../../components/ListPagination';
import { colors, fontFamilies, fontWeights } from '../../theme/tokens';
import DocumentsTable from '../Home/DocumentsTable';
import DocumentGrid from '../Home/DocumentGrid';
import { useDocumentActions } from '../Home/useDocumentActions';
import { useDocumentResolvers } from '../Home/useDocumentResolvers';
import MobileResultsTable from '../Home/MobileResultsTable';
import { SearchApiError, fetchSearch } from './api';
import { SearchCriteria, chipsFor, parseCriteria } from './criteria';
import {
  originPathFrom,
  searchDialogPathFor,
  searchResultsParams,
} from './paths';

// XD screen 7 ("7. Résultats" in DESIGN.md): full screen without
// tiles, "‹ Retour", the title, a removable chip per criterion, "Modifier
// ma recherche", the list / grid toggle and the common-structure documents
// table, with its pagination.
//
// The criteria and the page live in the query string (criteria.ts): removing
// a chip or changing page rewrites the URL and the listing is requested
// again; the link is shareable and the back button undoes each step.
//
// Decisions where the XD or the API give no data:
//   - Title: the XD writes "Résultats de votre recherches"; it is a typo
//     (a plural over "votre") and the singular is used.
//   - Chips: the term instead of "Mots clés #n"; the service and
//     issuer name instead of "Service #n" / "Emetteur #n". An issuer that
//     is not resolved (free text, or a USER without the accounts list) is
//     shown as-is.
//   - Grid: the "Flux 2" card (see Home/DocumentGrid); the toggle and its
//     stored preference are shared with the Home (DocumentsViewToggle).
//   - A backend 400 (invalid criteria in a hand-edited URL) is
//     shown instead of the list, with the link to correct them.
//   - Mobile: the smartphone XD screen S8 (DESIGN.md, "Diseño móvil") — a
//     white full-screen sheet with an orange "✕" back to the origin, the
//     criteria folded behind "Afficher mes critères", and the two-column
//     table with the expandable action band (MobileResultsTable). No app
//     header, no toggle. The pagination is ours (gap M-4).

export const SEARCH_RESULTS_KEY = 'SEARCH_RESULTS';

const readPositiveInt = (value: string | null, fallback: number): number => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : fallback;
};

const SearchResults: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, changeView] = useDocumentsView();
  // Mobile: the criteria fold behind "Afficher mes critères"; the artboard
  // draws them unfolded.
  const [criteriaOpen, setCriteriaOpen] = useState(true);

  useEffect(() => {
    document.title = 'ArchivOs - Résultats de votre recherche';
  }, []);

  // The URL is the source of truth; from it come the criteria and the page.
  const query = searchParams.toString();
  const criteria = useMemo(() => parseCriteria(searchParams), [query]); // eslint-disable-line react-hooks/exhaustive-deps
  const page = readPositiveInt(searchParams.get('page'), 1);
  const requestedLimit = readPositiveInt(searchParams.get('limit'), DEFAULT_PAGE_SIZE);
  const limit = PAGE_SIZE_OPTIONS.includes(requestedLimit)
    ? requestedLimit
    : DEFAULT_PAGE_SIZE;

  const { serviceName, userName, resolvers } = useDocumentResolvers();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery(
    [SEARCH_RESULTS_KEY, query],
    () => fetchSearch(criteria, { page, limit }),
    { keepPreviousData: true, retry: false },
  );

  const documents = data?.documents ?? [];
  const total = data?.totalItems ?? 0;
  const invalid = error instanceof SearchApiError && error.status === 400;

  // The route the search flow was entered from, threaded here by "Voir la
  // liste" (see paths.ts). "‹ Retour" leads there; the dialog links thread it
  // back so it survives any number of dialog ↔ results round trips.
  const origin = originPathFrom(location.state) ?? '/my-documents';

  const go = (next: SearchCriteria, pagination = { page: 1, limit }) => {
    // Rewriting the query string is a navigation: without restating it, the
    // origin state would be dropped on every chip removal or page change.
    setSearchParams(searchResultsParams(next, pagination), {
      state: { origin },
    });
  };

  const { onAction, elements: actionElements } = useDocumentActions({
    onDeleted: () => {
      // Last row of a page that is not the first: step back, or the
      // page would be left empty.
      if (documents.length === 1 && page > 1) {
        go(criteria, { page: page - 1, limit });
      } else {
        refetch();
      }
    },
    onVanished: () => refetch(),
  });

  const chips = chipsFor(criteria, {
    serviceName,
    issuerName: (value) => {
      const name = userName(value);
      return name === '—' ? value : name;
    },
  });

  const modifyPath = searchDialogPathFor(criteria);
  const here = `${location.pathname}${location.search}`;

  const list = isMobile ? (
    <MobileResultsTable
      documents={documents}
      isLoading={isLoading}
      isError={isError}
      resolvers={resolvers}
      onAction={onAction}
      onRetry={() => refetch()}
    />
  ) : view === 'grid' ? (
    <DocumentGrid
      documents={documents}
      isLoading={isLoading}
      isError={isError}
      resolvers={resolvers}
      onAction={onAction}
      onRetry={() => refetch()}
    />
  ) : (
    <DocumentsTable
      documents={documents}
      isLoading={isLoading}
      isError={isError}
      hasFilters
      resolvers={resolvers}
      onAction={onAction}
      onRetry={() => refetch()}
    />
  );

  const progressEl = (
    <Box sx={{ height: 4, mt: -1 }}>
      {isFetching && !isLoading && (
        <LinearProgress color="secondary" sx={{ height: 2 }} />
      )}
    </Box>
  );

  const invalidAlert = (
    <Alert severity="warning" variant="outlined">
      Les critères de cette recherche ne sont pas valides.{' '}
      <Link
        component={RouterLink}
        to={modifyPath}
        state={{ from: here, origin }}
        underline="hover"
      >
        Modifier ma recherche
      </Link>
    </Alert>
  );

  const paginationEl = (
    <ListPagination
      count={total}
      page={page - 1}
      pageSize={limit}
      onPageChange={(next) => go(criteria, { page: next + 1, limit })}
      onPageSizeChange={(size) => go(criteria, { page: 1, limit: size })}
    />
  );

  // Mobile: the S8 sheet — no app header, the "✕" leads back to the origin.
  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.white,
          px: 2,
          pt: 1,
          pb: 4,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            aria-label="Fermer"
            onClick={() => navigate(origin)}
            sx={{ color: colors.accent }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography
          component="h2"
          sx={{
            color: colors.primary,
            fontWeight: fontWeights.semiBold,
            fontSize: '1.375rem',
            lineHeight: 1.3,
            pb: 1,
            mb: 2,
            borderBottom: `1px solid ${colors.primary}`,
          }}
        >
          Résultats de votre recherche
        </Typography>

        {chips.length === 0 ? (
          <Typography sx={{ color: colors.muted, fontSize: '0.875rem', mb: 1 }}>
            Aucun critère : tous les documents visibles.
          </Typography>
        ) : (
          <>
            <Box
              component="button"
              type="button"
              onClick={() => setCriteriaOpen((open) => !open)}
              aria-expanded={criteriaOpen}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                color: colors.accent,
                fontWeight: fontWeights.bold,
                fontSize: '0.875rem',
                mb: 1,
              }}
            >
              Afficher mes critères
              <ArrowDropDownIcon
                sx={{ transform: criteriaOpen ? 'none' : 'rotate(-90deg)' }}
              />
            </Box>
            <Collapse in={criteriaOpen}>
              <Box
                component="ul"
                aria-label="Critères appliqués"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  mb: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {chips.map((chip) => (
                  <li key={chip.key}>
                    <Chip
                      label={chip.label}
                      variant="outlined"
                      onDelete={() => go(chip.remove(criteria))}
                      data-testid="criterion-chip"
                      sx={{
                        color: colors.primary,
                        borderColor: colors.primary,
                        fontWeight: fontWeights.medium,
                        '& .MuiChip-deleteIcon': { color: colors.accent },
                      }}
                    />
                  </li>
                ))}
              </Box>
            </Collapse>
          </>
        )}

        <Link
          component={RouterLink}
          to={modifyPath}
          state={{ from: here, origin }}
          variant="actionLink"
          underline="hover"
          sx={{ color: colors.accent, display: 'inline-block', mb: 2 }}
        >
          Modifier ma recherche
        </Link>

        {progressEl}
        {invalid ? (
          invalidAlert
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {list}
            {paginationEl}
          </Box>
        )}

        {actionElements}
      </Box>
    );
  }

  return (
    <Container showTiles={false}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Back out of the search flow, not into the dialog: the dialog is
            one step deeper ("Modifier ma recherche"), and pointing "Retour"
            at it left the user looping between the two with no way home. */}
        <Link
          component={RouterLink}
          to={origin}
          variant="actionLink"
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            color: colors.accentLight,
          }}
        >
          <ChevronLeftIcon fontSize="small" />
          Retour
        </Link>

        <Typography
          component="h2"
          variant="h2"
          // XD: the page title is Poppins like the body, not the serif of
          // the h2 (modal heading) style.
          sx={{
            color: colors.primary,
            fontFamily: fontFamilies.sans,
            fontWeight: fontWeights.semiBold,
          }}
        >
          Résultats de votre recherche
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* XD: the link sits at the left, before the chips. */}
          <Link
            component={RouterLink}
            to={modifyPath}
            state={{ from: here, origin }}
            variant="actionLink"
            underline="hover"
            sx={{ color: colors.accentLight }}
          >
            Modifier ma recherche
          </Link>

          {chips.length === 0 ? (
            <Typography sx={{ color: colors.muted, fontSize: '0.875rem' }}>
              Aucun critère : tous les documents visibles.
            </Typography>
          ) : (
            <Box
              component="ul"
              aria-label="Critères appliqués"
              sx={{
                listStyle: 'none',
                m: 0,
                p: 0,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {chips.map((chip) => (
                <li key={chip.key}>
                  <Chip
                    label={chip.label}
                    variant="outlined"
                    onDelete={() => go(chip.remove(criteria))}
                    data-testid="criterion-chip"
                    sx={{ color: colors.textBase, fontWeight: fontWeights.medium }}
                  />
                </li>
              ))}
            </Box>
          )}
        </Box>

        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <DocumentsViewToggle view={view} onChange={changeView} />
          </Box>
        )}

        {/* Progress when changing page or criterion, without emptying the list */}
        {progressEl}

        {invalid ? (
          invalidAlert
        ) : (
          <>
            {list}
            {paginationEl}
          </>
        )}
      </Box>

      {actionElements}
    </Container>
  );
};

export default SearchResults;
